/// <amd-module name="Wit/Common/QueryListServices/IQueryListService" />
import { getTFSPageData } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { ITreeItem, ITreeItemAdd, ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { QueryListTreeItemProvider } from "Wit/Common/QueryListServices/QueryListTreeItemProvider";
import { QueryExpand, QueryHierarchyItem, QueryHierarchyItemsResult } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient, IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import * as Resources from "Wit/Common/QueryListServices/Resources";
import { IQuerySaveDialogProps } from "Wit/Common/QuerySaveDialog/Components/QuerySaveDialog.Props";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import {
    EmptyItem,
    ExtendedHierarchyItem,
    IQueryListProviderResponse,
    ObservableFavoriteItemsGroup,
    QueryItem,
    QuerySaveDialogMode
} from "Wit/Common/QueryUtils/Models";
import * as QueriesHubConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import { getEditorUrl, getQueryUrl, onClickQuerySaveFPS } from "Wit/Common/QueryUtils/RoutingUtils";
import * as StringUtils from "VSS/Core/Util/String";

export const c_myFavoritesGroupKey = "MY_FAVORITES_GROUP";

export interface IQueryListService extends IVssService {
    isDialogOpen: IReadonlyObservableValue<boolean>;
    isLoading: IReadonlyObservableValue<boolean>;
    queryFolderItem: ObservableValue<QueryHierarchyItem | undefined>;
    queryTree: QueryListTreeItemProvider;
    favoriteGroups: IReadonlyObservableArray<ObservableFavoriteItemsGroup>;
    saveQueryDialogProps: IReadonlyObservableValue<IQuerySaveDialogProps>;
    rootMyQueriesFolderId: string;
    addFavorite(item: QueryHierarchyItem): void;
    addTeamFavorite(queryItem: QueryItem, teamId: string, teamName: string): void;
    removeFavorite(item: QueryHierarchyItem): void;
    removeTeamFavorite(queryItem: QueryItem, teamId: string, teamName: string): void;
    canDrag(item: QueryItem | EmptyItem): boolean;
    deleteQuery(item: QueryItem): void;
    createQuery(item: QueryHierarchyItem, parentPath: string, navigateToQueryOnSave?: boolean): Promise<QueryHierarchyItem | undefined>;
    getQueries(): void;
    getQueriesAsync(): Promise<void>;
    getSharedQueriesByProjectIDAsync(projectID: string): Promise<void>;
    getQueryByPath(queryPath: string, projectID?: string | undefined): Promise<QueryHierarchyItem | undefined>;
    getQueryChildren(queryItem: ITreeItem<QueryItem | EmptyItem>, ignoreSavedData?: boolean): Promise<ITreeItem<QueryItem | EmptyItem> | undefined>;
    getQueryWithChildren(queryItem: QueryItem, ignoreSavedData?: boolean): Promise<QueryItem>;
    getTreeItemByPath(folderPath: string): Promise<ITreeItem<QueryItem | EmptyItem> | undefined>;
    moveQuery(originalQueryItem: ITreeItemEx<QueryItem>, targetFolderItem: ITreeItem<QueryItem | EmptyItem>, newName?: string): Promise<QueryItem>;
    onDismiss(): void;
    onNewFolderClick(pivot: string, queryItem?: QueryHierarchyItem, onDialogDismissCallback?: () => void): void;
    onRenameItemClick(pivot: string, queryItem: QueryItem, onDialogDismissCallback?: () => void): void;
    onEditorClick(pivot: string, isNewQuery: boolean, id?: string): void;
    onQueryClick(pivot: string, id: string): void;
    searchQueries(searchText: string): Promise<QueryHierarchyItemsResult | undefined>;
    toggleTreeItem(treeItem: ITreeItemEx<QueryItem | EmptyItem>): Promise<void>;
    updateQuery(updatedQueryItem: QueryItem, originalQueryItem: ITreeItemEx<QueryItem>): Promise<QueryItem>;
}

const c_queryListDataProvider = "ms.vss-work-web.query-list-data-provider";

class QueryListService extends VssService implements IQueryListService {
    private _isDialogOpen = new ObservableValue(false);
    private _isLoading = new ObservableValue(true);
    private _saveQueryDialogProps = new ObservableValue<IQuerySaveDialogProps>({ mode: QuerySaveDialogMode.NewFolder });
    private _queryTree = new QueryListTreeItemProvider([]);
    private _favoriteGroups = new ObservableArray<ObservableFavoriteItemsGroup>([]);
    private _workItemTrackingClient: IWorkItemTrackingRestClient;
    private _expandedFolders: string[] = [];
    private _queryFolderItem: ObservableValue<QueryHierarchyItem | undefined> = new ObservableValue(undefined);
    private _queryItemPromisesByPath = new Map<string, Promise<QueryHierarchyItem | undefined>>();
    private _queryItemPromisesById = new Map<string, Promise<QueryHierarchyItem | undefined>>();
    private _changedTreeItemsPaths: Set<string> = new Set<string>();

    public get isDialogOpen(): IReadonlyObservableValue<boolean> {
        return this._isDialogOpen;
    }

    public get isLoading(): IReadonlyObservableValue<boolean> {
        return this._isLoading;
    }

    public get queryFolderItem(): ObservableValue<QueryHierarchyItem | undefined> {
        return this._queryFolderItem;
    }

    public get queryTree(): QueryListTreeItemProvider {
        return this._queryTree;
    }

    public get favoriteGroups(): IReadonlyObservableArray<ObservableFavoriteItemsGroup> {
        return this._favoriteGroups;
    }

    public get saveQueryDialogProps(): IReadonlyObservableValue<IQuerySaveDialogProps> {
        return this._saveQueryDialogProps;
    }

    public get rootMyQueriesFolderId(): string {
        return (this._queryTree.roots[0].data as QueryItem).id;
    }

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this._workItemTrackingClient = getWorkItemTrackingClient(pageContext);
    }

    public addFavorite(queryItem: QueryItem) {
        if (this._favoriteGroups.value.length > 0) {
            this._favoriteGroups.value.forEach(group => {
                let item = group.items.find(item => {
                    return item.id === queryItem.id;
                });
                if (item) {
                    item.isFavoriteObservable.value = true;
                }
                else if (!item && group.id === QueriesHubConstants.MyFavoritesGroupKey) {
                    group.items.splice(this._favoriteGroups.length, 0, queryItem);
                }
            });
        }
        this._findFavoriteInTree(queryItem.id, true);
    }

    public addTeamFavorite(queryItem: QueryItem, teamId: string, teamName: string) {
        const team = this._favoriteGroups.value.find(group => {
            return group.id === teamId;
        });

        const notificationTeam = {
            id: teamId,
            name: teamName,
            items: [queryItem]
        };
        if (team) {
            team.items.splice(team.items.length, 0, queryItem);
        } else {
            this._favoriteGroups.splice(this._favoriteGroups.length, 0, notificationTeam);
        }

        this._favoriteGroups.notify({ index: 0, changedItems: [notificationTeam] }, "push");
    }

    public removeFavorite(queryItem: QueryItem) {
        this._favoriteGroups.value.forEach(group => {
            const index = group.items.findIndex(item => {
                return item.id === queryItem.id;
            });
            if (index != -1) {
                group.items[index].isFavoriteObservable.value = false;
                if (group.id === QueriesHubConstants.MyFavoritesGroupKey) {
                    group.items.splice(index, 1);
                }
            }
        });
        this._findFavoriteInTree(queryItem.id, false);
    }

    public removeTeamFavorite(queryItem: QueryItem, teamId: string, teamName: string) {
        let noChildrenLeft = false;
        const team = this._favoriteGroups.value.find(group => {
            return group.id === teamId;
        });
        if (team) {
            let index = team.items.findIndex(item => {
                return item.id === queryItem.id;
            });
            if (team.items.length === 1) {
                noChildrenLeft = true;
            }
            team.items.splice(index, 1);
        }

        const teamGroupIndex = this._favoriteGroups.value.findIndex(group => group.id === teamId);
        if (teamGroupIndex > -1 && noChildrenLeft) {
            this._favoriteGroups.splice(teamGroupIndex, 1);
        }

        const notificationTeam = {
            id: teamId,
            name: teamName,
            items: [queryItem]
        };
        this._favoriteGroups.notify({ index: 0, changedItems: [notificationTeam] }, "pop");
    }

    public canDrag(item: QueryItem | EmptyItem) {
        return !item.isLoadingContext && !item.isEmptyFolderContext && !item.isFolder;
    }

    public getQueries(): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<IQueryListProviderResponse>(c_queryListDataProvider);
        this.loadQueries(data);
    }

    public async getQueriesAsync(): Promise<void> {
        this._isLoading.value = true;
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = await contributionService.getDataAsync<IQueryListProviderResponse>(c_queryListDataProvider, { loadAllQueries: true });
        this.loadQueries(data);
    }

    public async getSharedQueriesByProjectIDAsync(projectID: string): Promise<void> {
        this._isLoading.value = true;
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = await contributionService.getDataAsync<IQueryListProviderResponse>(c_queryListDataProvider, {
            loadAllQueries: true,
            projectID: projectID,
            loadSharedQueries: true
        });
        this.loadQueries(data);
    }

    private loadQueries(data?: IQueryListProviderResponse) {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        if (!data) {
            const dataProviderException = contributionService.getDataProviderExceptions()[c_queryListDataProvider];
            if (dataProviderException) {
                reportError(this.pageContext, "New-Queries-Hub", dataProviderException.message);
            }
        }
        if (data?.allItems) {
            const queryItems: QueryItem[] = convertHierarchyItemsToQueryItems(data.allItems);
            queryItems.forEach(root => {
                root.children = convertHierarchyItemsToQueryItems(root.children);
            });
            this._queryTree = new QueryListTreeItemProvider(this._convertQueriesToTreeItems(queryItems, 0));
        }
        if (data?.queryFavoriteGroups) {
            this._favoriteGroups.value = data?.queryFavoriteGroups.map(favoriteGroup => {
                const queryItems: QueryItem[] = favoriteGroup.items.map(item => {
                    return {
                        ...item,
                        depth: 0,
                        isFavoriteObservable: new ObservableValue<boolean>(item.isFavorite ?? false),
                        isEmptyFolderContext: false,
                        isLoadingContext: false
                    };
                });
                return {
                    id: favoriteGroup.id,
                    name: favoriteGroup.name,
                    items: queryItems
                };
            });
        }
        this._queryFolderItem.value = data?.queryItem || this._queryFolderItem.value;
        this._isLoading.value = false;
    }

    public async createQuery(item: QueryHierarchyItem, parentPath: string, navigateToQueryOnSave?: boolean): Promise<QueryHierarchyItem | undefined> {
        const projectId = getTFSPageData(this.pageContext)?.project?.id;
        if (projectId) {
            const hierarchyItem = await this._workItemTrackingClient.createQuery(item, projectId, parentPath);
            const parentItem = this._queryTree.value.find(query => {
                return !query.underlyingItem.data.isEmptyFolderContext && query.underlyingItem.data.path === parentPath;
            });
            if (parentItem) {
                const treeItem = this._convertQueriesToTreeItems([hierarchyItem as QueryItem], parentItem.depth + 1);
                // if we move the query to an empty folder, remove the empty marker when we splice in the query
                const emptyChildrenElements = parentItem?.underlyingItem.childItems?.filter(item => {
                    return item.data.isEmptyFolderContext;
                });
                this._queryTree.splice(parentItem?.underlyingItem, emptyChildrenElements, [{ items: treeItem }]);
                if (this._queryFolderItem.value && this._queryFolderItem.value.id === (parentItem.underlyingItem.data as QueryItem).id) {
                    this.queryFolderItem.value?.children.unshift(treeItem[0].data as QueryItem);
                    this._queryFolderItem.notify(this._queryFolderItem.value, "add");
                }
            }
            if (navigateToQueryOnSave) {
                const url = getEditorUrl(this.pageContext, hierarchyItem.id);
                onClickQuerySaveFPS(this.pageContext, url, true);
            }
            return hierarchyItem;
        } else {
            reportError(this.pageContext, "create-query", Resources.ProjectNotFoundError);
            return undefined;
        }
    }

    public deleteQuery(item: QueryItem): void {
        const treeItem = this._queryTree.value.find(treeItem => {
            return !treeItem.underlyingItem.data.isEmptyFolderContext && treeItem.underlyingItem.data.id === item.id;
        });

        if (!treeItem) {
            // Shouldn't ever end up here but just in case
            reportError(this.pageContext, "delete-query", Resources.QueryItemNotFoundError);
            return;
        }

        this._queryTree.remove(treeItem.underlyingItem);
        if (this._queryFolderItem) {
            const itemIndex =
                this.queryFolderItem.value?.children?.findIndex(child => child.id === (treeItem.underlyingItem.data as QueryItem).id) ?? undefined;
            if (itemIndex) {
                this._queryFolderItem.value!.children.splice(itemIndex, 1);
                this._queryFolderItem.notify(this._queryFolderItem.value, "remove");
            }
        }
        this._updateFavoriteGroups(item);
    }

    public async getQueryByPath(queryPath: string, projectID?: string): Promise<QueryHierarchyItem | undefined> {
        const projectId = projectID ? projectID : getTFSPageData(this.pageContext)?.project?.id;
        const queryPromise = this._queryItemPromisesByPath.get(queryPath);
        if (queryPromise) {
            return queryPromise;
        } else if (projectId) {
            const queryItem = this._workItemTrackingClient.getQuery(projectId, queryPath, QueryExpand.Wiql, 0);
            this._queryItemPromisesByPath.set(queryPath, queryItem);
            return queryItem;
        } else {
            reportError(this.pageContext, "loading-query-path", Resources.ProjectNotFoundError);
        }
    }

    public async getQueryChildren(queryItem: ITreeItem<QueryItem | EmptyItem>, ignoreSavedData?: boolean): Promise<ITreeItem<QueryItem | EmptyItem> | undefined> {
        const queryWithChildren = await this.getQueryWithChildren(queryItem.data as QueryItem, ignoreSavedData);
        return this._convertQueriesToTreeItems([queryWithChildren], (queryItem.data as QueryItem).depth)[0];
    }

    public async getQueryWithChildren(queryItem: QueryItem, ignoreSavedData?: boolean): Promise<QueryItem> {
        const projectId = getTFSPageData(this.pageContext)?.project?.id;
        if (projectId) {
            try {
                let queryWithChildrenPromise = this._queryItemPromisesById.get(queryItem.id);
                if (!queryWithChildrenPromise || ignoreSavedData) {
                    queryWithChildrenPromise = this._workItemTrackingClient.getQuery(projectId, queryItem.id, QueryExpand.Wiql, 2);
                    this._queryItemPromisesById.set(queryItem.id, queryWithChildrenPromise);
                }

                const queryWithChildren = await queryWithChildrenPromise;
                if (queryWithChildren) {
                    this._queryItemPromisesByPath.set(queryWithChildren.path, Promise.resolve(queryWithChildren));
                }
                const hierarchyItem = {
                    ...queryWithChildren,
                    depth: queryItem.depth,
                    isLoadingContext: false,
                    isEmptyFolderContext: false,
                    isFavoriteObservable: queryItem.isFavoriteObservable
                } as QueryItem;
                const childItems: QueryItem[] | undefined = hierarchyItem.children.map(item => {
                    const itemIsFavorite = this._isFavorite(item.id);
                    return {
                        ...item,
                        depth: 0,
                        isFavoriteObservable: new ObservableValue<boolean>(itemIsFavorite),
                        isEmptyFolderContext: false,
                        isLoadingContext: false
                    };
                });
                hierarchyItem.children = childItems;
                return hierarchyItem;
            } catch (error) {
                error = error as Error;
                reportError(this.pageContext, "loading-query-children", error.message);
            }
        }
        return queryItem;
    }

    /**
     *
     * @param originalQueryItem source item to be moved
     * @param targetTreeItem target item to which source is moved to
     * @param newName Optional, alternate name other than source item
     */
    public moveQuery = async (
        originalQueryItem: ITreeItemEx<QueryItem>,
        targetTreeItem: ITreeItem<QueryItem>,
        newName?: string
    ): Promise<QueryItem> => {
        this._queryItemPromisesByPath.delete(originalQueryItem.underlyingItem.data.path);
        this._queryItemPromisesById.delete(originalQueryItem.underlyingItem.data.id);

        // move the query & it's folder
        const query = await this._moveQuery(originalQueryItem, targetTreeItem, newName);
        query.isFavoriteObservable = query.isFavoriteObservable || new ObservableValue<boolean>(this._isFavorite(query.id));
        // convert query to tree item
        const treeItem = this._convertQueriesToTreeItems([query], query.depth);
        // if the original query is the only query in this folder, splice in an empty marker when we remove the query
        const emptyMarker =
            originalQueryItem.parentItem?.underlyingItem.childItems?.length! <= 1
                ? [
                    {
                        items: [
                            {
                                data: { isEmptyFolderContext: true, isLoadingContext: false } as EmptyItem
                            }
                        ]
                    }
                ]
                : undefined;
        this._queryTree.splice(originalQueryItem.parentItem?.underlyingItem, [originalQueryItem.underlyingItem], emptyMarker);

        // if we move the query to an empty folder, remove the empty marker when we splice in the query
        const emptyChildrenElements = targetTreeItem.childItems?.filter(item => {
            return item.data.isEmptyFolderContext;
        });

        // Define where we should insert an item
        const childItems = targetTreeItem.childItems;
        let insertAfter: ITreeItem<QueryItem | EmptyItem> | undefined = undefined;
        if (childItems && !originalQueryItem.underlyingItem.data.isFolder) {
            let length = childItems.length;
            while (--length >= 0) {
                if (childItems[length].data.isFolder) {
                    insertAfter = childItems[length];
                    break;
                }
            }
        }

        this._queryTree.splice(targetTreeItem, emptyChildrenElements, [{ insertAfter, items: treeItem }]);
        this._updateFavoriteGroups(originalQueryItem.underlyingItem.data, query);
        if (targetTreeItem.childItems) {
            this._updateFavoriteStatusForChildItems(targetTreeItem.childItems.filter(item => item.data.id === query.id));
        }

        return query;
    };

    public onDismiss = () => {
        this._isDialogOpen.value = false;
    };

    public onNewFolderClick = (pivot: string, queryItem?: QueryHierarchyItem, onDialogDismissCallback?: () => void) => {
        this._isDialogOpen.value = true;
        this._saveQueryDialogProps.value = {
            mode: QuerySaveDialogMode.NewFolder,
            onDismiss: () => {
                this.onDismiss();
                onDialogDismissCallback?.();
            },
            queryItem: queryItem
        };
    };

    public onRenameItemClick = (pivot: string, queryItem: QueryItem, onDialogDismissCallback?: () => void) => {
        this._isDialogOpen.value = true;
        this._saveQueryDialogProps.value = {
            mode: queryItem.isFolder ? QuerySaveDialogMode.RenameFolder : QuerySaveDialogMode.RenameQuery,
            onDismiss: () => {
                this.onDismiss();
                onDialogDismissCallback?.();
            },
            queryItem: queryItem
        };
    };

    public onEditorClick = (pivot: string, isNewQuery: boolean, queryId: string) => {
        const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService &&
            telemetryService.publishEvent("bolt-new-queries", "BoltNewQueries.QueryAction", {
                key: isNewQuery ? "new-query" : "edit-query",
                pivot: pivot,
                source: "command-button"
            });

        let url: string;
        if (isNewQuery) {
            url = getEditorUrl(this.pageContext, /* queryId */ undefined, /* parent id */ queryId);
        } else {
            url = getEditorUrl(this.pageContext, queryId);
        }
        onClickFPS(this.pageContext, url, true);
    };

    public onQueryClick = (pivot: string, queryId: string) => {
        const url = getQueryUrl(this.pageContext, queryId);
        onClickFPS(this.pageContext, url, true);
    };

    public toggleTreeItem = async (treeItem: ITreeItemEx<QueryItem | EmptyItem>) => {
        // we know this is a QueryItem because we are toggling it
        const item = treeItem.underlyingItem as ITreeItem<QueryItem>;
        const isChangedTreeItem = this._changedTreeItemsPaths.has(item.data.path);
        if (item.data.hasChildren && !item.data.children || isChangedTreeItem) {
            this._changedTreeItemsPaths.delete(item.data.path);

            // add in a loading spinner while we get the children
            const childItems = treeItem.underlyingItem.childItems?.map(child => {
                child.data.isEmptyFolderContext = true;
                child.data.isLoadingContext = true;
                return {
                    ...child
                };
            }) as ITreeItem<QueryItem | EmptyItem>[];
            this._queryTree.splice(treeItem.underlyingItem, treeItem.underlyingItem.childItems, [
                {
                    items: childItems
                }
            ]);
            // get the children
            this._updateQueryChildren(treeItem as ITreeItemEx<QueryItem>);
        }
        item.childItems && this._updateFavoriteStatusForChildItems(item.childItems);
        
        this._queryTree.toggle(item);
        if (item.expanded) {
            this._expandedFolders.push(item.data.id);
        } else {
            const folderId = item.data.id;
            const folderIndex = this._expandedFolders.indexOf(folderId);
            if (folderIndex > -1) {
                this._expandedFolders.splice(folderIndex, 1);
            }
        }
    };

    public async getTreeItemByPath(folderPath: string): Promise<ITreeItem<QueryItem | EmptyItem> | undefined> {
        const folders = folderPath.split(QueriesHubConstants.QueryFoldersDelimiter);
        this._changedTreeItemsPaths.add(folderPath);

        const queryItem = await this.getQueryByPath(folderPath);
        let item: ITreeItem<QueryItem | EmptyItem> | undefined;
        if (queryItem) {
            const convertedTreeItems = this._convertQueriesToTreeItems(convertHierarchyItemsToQueryItems([queryItem]), folders.length - 1);
            item = convertedTreeItems?.length > 0 ? convertedTreeItems[0] : undefined;
        }
        
        return item;
    }

    public async updateQuery(updatedQueryItem: QueryItem, originalQueryItem?: ITreeItemEx<QueryItem>): Promise<QueryItem> {
        try {
            const projectId = getTFSPageData(this.pageContext)?.project?.id;
            if (!projectId) {
                // we need a project id to save
                throw new Error(Resources.ProjectNotFoundError);
            }

            const updatedQuery = await this._workItemTrackingClient.updateQuery(
                updatedQueryItem as QueryHierarchyItem,
                projectId,
                updatedQueryItem.id
            );

            const queryItem: QueryItem = {
                ...updatedQuery,
                depth: originalQueryItem?.underlyingItem.data.depth ?? updatedQueryItem.depth,
                isEmptyFolderContext: false,
                isLoadingContext: false,
                isFavoriteObservable: originalQueryItem?.underlyingItem.data.isFavoriteObservable ?? updatedQueryItem.isFavoriteObservable
            };
            queryItem.children = originalQueryItem?.underlyingItem.data.children ?? updatedQueryItem.children;
            if (originalQueryItem) {
                const treeItem = this._convertQueriesToTreeItems([queryItem as QueryItem], originalQueryItem.depth);
                this._queryTree.splice(
                    originalQueryItem.parentItem?.underlyingItem,
                    [originalQueryItem.underlyingItem],
                    [{ items: treeItem, insertAfter: originalQueryItem.underlyingItem }]
                );

                this._updateFavoriteGroups(originalQueryItem.underlyingItem.data, queryItem);
            }
            return queryItem as QueryItem;
        } catch (error) {
            error = error as Error;
            reportError(this.pageContext, "move-queries", error.message);
            // return original item
            return originalQueryItem?.underlyingItem.data ?? updatedQueryItem;
        }
    }

    public searchQueries = async (searchText: string) => {
        const projectId = getTFSPageData(this.pageContext)?.project?.id;
        if (!projectId) {
            // we need a project id to search
            reportError(this.pageContext, "search-queries", Resources.ProjectNotFoundError);
            return;
        }
        return await this._workItemTrackingClient.searchQueries(projectId, searchText, QueriesHubConstants.MaximumQueriesToShow, QueryExpand.Wiql);
    };

    private _convertQueriesToTreeItems(queries: QueryItem[], depth: number) {
        let treeItems: ITreeItem<QueryItem | EmptyItem>[] = [];
        if (queries) {
            const sortedQueries = queries.sort((a, b) => StringUtils.localeComparer(a.name, b.name));
            const folders = sortedQueries.filter((query: QueryItem) => query.isFolder);
            const flatQueries = sortedQueries.filter((query: QueryItem) => !query.isFolder);
            if (folders) {
                folders.forEach((folder: QueryItem) => {
                    const expanded = depth === 0 ? true : this._isExpanded(folder.id);
                    const treeItem = {
                        // add self as child if the folder is empty so we can detect it and render an empty row
                        childItems: this._convertQueriesToTreeItems(folder.children as QueryItem[], depth + 1),
                        expanded: expanded,
                        data: { ...folder, depth: depth }
                    } as ITreeItem<QueryItem>;
                    treeItems.push(treeItem);
                });
            }
            if (flatQueries) {
                flatQueries.forEach((query: QueryItem) => {
                    const url = getQueryUrl(this.pageContext, query.id);
                    treeItems.push({
                        data: { ...query, linkProps: { href: url } }
                    });
                });
            }
        } else {
            treeItems.push({
                data: { isEmptyFolderContext: true, isLoadingContext: false }
            });
        }

        return treeItems;
    }

    private _findFavoriteInTree = (queryId: string, favorite: boolean): void => {
        const item = this._queryTree.value.find(item => {
            return !item.underlyingItem.data.isEmptyFolderContext && item.underlyingItem.data.id === queryId;
        });
        if (item && !item.underlyingItem.data.isEmptyFolderContext) {
            item.underlyingItem.data.isFavoriteObservable.value = favorite;
        }
    };

    private _isExpanded = (id: string): boolean => {
        return this._expandedFolders.indexOf(id) > -1;
    };

    private async _updateQueryChildren(queryItem: ITreeItemEx<QueryItem | EmptyItem>, ignoreSavedData?: boolean) {
        const queryTreeItem = await this.getQueryChildren(queryItem.underlyingItem, ignoreSavedData);
        if (queryTreeItem) {
            this._queryTree.splice(queryItem.parentItem?.underlyingItem, [queryItem.underlyingItem], [
                {
                    insertAfter: queryItem.underlyingItem,
                    items: [queryTreeItem]
                }
            ] as ITreeItemAdd<QueryItem>[]);
        }
    }

    private async _moveQuery(
        originalQueryItem: ITreeItemEx<QueryItem>,
        targetFolderItem: ITreeItem<QueryItem | EmptyItem>,
        newName?: string
    ): Promise<QueryItem> {
        if (targetFolderItem.data.isEmptyFolderContext) {
            return originalQueryItem.underlyingItem.data;
        }

        const target = targetFolderItem as ITreeItem<QueryItem>;
        const sourceFolder = originalQueryItem.underlyingItem.data.path;
        const targetFolder = target.data.path;
        const originalPathWithEndSlash = originalQueryItem.underlyingItem.data.path.endsWith(QueriesHubConstants.QueryFoldersDelimiter)
            ? originalQueryItem.underlyingItem.data.path
            : originalQueryItem.underlyingItem.data.path + QueriesHubConstants.QueryFoldersDelimiter;
        if (
            sourceFolder === targetFolder || // Try to move to the same location
            // Try to move to its own child folder. We neeed to be careful here to not also block folders that are substring of others.
            // Lets say we have two folder:  SQ/AAAAAA and SQ/AAA, in this case its fine to move AAA into AAAAAA. However, we don't want to be able
            // to move SQ/AAAAAA into SQ/AAAAAA/BBB. The code used to just to targetFolder.indexOf(originalQueryItem.path) which blocks the nesting case, but also
            // blocks moving AAA into AAAAAA. To fix that I only do the check if its a folder and in that case I append a trailing slash to ensure I am matching
            // a folder path and not the leaf query

            (originalQueryItem.underlyingItem.data.isFolder && targetFolder.indexOf(originalPathWithEndSlash) === 0) ||
            originalQueryItem.underlyingItem.data.path === targetFolder
        ) {
            // Try to move to itself
            return originalQueryItem.underlyingItem.data;
        }

        const isPublic = target.data.isPublic;

        // Update query item and store data
        newName = newName || originalQueryItem.underlyingItem.data.name;
        const updatedQueryItem: QueryItem = {
            ...originalQueryItem.underlyingItem.data,
            isPublic: isPublic,
            name: newName,
            path: `${target.data.path}/${newName}`
        };

        // For moving, we check if the target already exists before actually updating the stores
        if (
            this._queryTree.value.find(element => {
                !element.underlyingItem.data.isEmptyFolderContext && element.underlyingItem.data.path === updatedQueryItem.path;
            })
        ) {
            throw new Error(Resources.QueryItemNotFoundError);
        }

        try {
            const projectId = getTFSPageData(this.pageContext)?.project?.id;
            if (!projectId) {
                // we need a project id to save
                throw new Error(Resources.ProjectNotFoundError);
            }

            const updatedQuery = await this._workItemTrackingClient.createQuery(
                updatedQueryItem as QueryHierarchyItem,
                projectId,
                target.data.id
            );

            return updatedQuery as QueryItem;
        } catch (error) {
            error = error as Error;
            reportError(this.pageContext, "move-queries", error.message);
            // return original item
            return originalQueryItem.underlyingItem.data;
        }
    }

    private _updateFavoriteGroups = (item: QueryItem, itemToAdd?: QueryItem) => {
        this._favoriteGroups.value.forEach((group, index) => {
            const originalItemIndex = group.items.findIndex(queryItem => {
                return queryItem.id === item.id;
            });
            if (originalItemIndex !== -1) {
                if (itemToAdd) {
                    group.items.splice(originalItemIndex, 1, itemToAdd);
                } else {
                    group.items.splice(originalItemIndex, 1);
                }

                this._favoriteGroups.notify({ index: index }, "change");
            }
        });
    };

    private _isFavorite(itemId: string): boolean {
        return !!this._favoriteGroups.value
            .find(group => group.id === QueriesHubConstants.MyFavoritesGroupKey)
            ?.items.some(queryItem => queryItem.id === itemId);
    };

    private _updateFavoriteStatusForChildItems (items: ITreeItem<QueryItem>[]): void {
        for (const child of items) {
            if (!child.data.isFavoriteObservable && child.data.id) {
                child.data.isFavoriteObservable = new ObservableValue<boolean>(this._isFavorite(child.data.id));
            }
        }
    }
}

function convertHierarchyItemsToQueryItems(hierarchyItems?: ExtendedHierarchyItem[]): QueryItem[] {
    return hierarchyItems
        ? hierarchyItems.map(item => {
            return {
                ...item,
                depth: 0,
                isFavoriteObservable: new ObservableValue<boolean>(item.isFavorite ?? false),
                isEmptyFolderContext: false,
                isLoadingContext: false
            };
        })
        : [];
}

Services.add("IQueryListService", { serviceFactory: QueryListService });
